import { useState, useEffect, useMemo, useCallback } from 'react';
import { TickerItem, severityConfig } from '../services/serviceHealthTypes';
import { vendors, vendorIdMap, VendorDefinition } from '../services/vendorProducts';
import {
  getHiddenVendorProducts as getHiddenFromAPI,
  setHiddenVendorProducts as saveHiddenToAPI,
  getTickerSpeed,
  setTickerSpeed as saveTickerSpeed,
  TICKER_SPEED_MIN,
  TICKER_SPEED_MAX,
  TICKER_SPEED_DEFAULT,
} from '../services/userPreferences';

interface ServiceHealthAllModalProps {
  items: TickerItem[];
  onItemClick: (item: TickerItem) => void;
  onClose: () => void;
}

// Check if an item should be shown based on hidden vendor:products
export function isItemVisible(item: TickerItem, hiddenProducts: Set<string>): boolean {
  // Map old vendor ID to new consolidated one
  const vendorId = vendorIdMap[item.vendor] || item.vendor;

  // Check if any of the affected services are hidden
  for (const service of item.affectedServices) {
    const key = `${vendorId}:${service}`;
    if (hiddenProducts.has(key)) {
      return false;
    }
  }

  return true;
}

// Re-export for backward compatibility with ServiceHealthTicker
export async function getHiddenVendorProducts(): Promise<Set<string>> {
  return getHiddenFromAPI();
}

export async function saveHiddenVendorProducts(hidden: Set<string>): Promise<void> {
  return saveHiddenToAPI(hidden);
}

export default function ServiceHealthAllModal({ items, onItemClick, onClose }: ServiceHealthAllModalProps) {
  const [showSettings, setShowSettings] = useState(false);
  const [hiddenProducts, setHiddenProducts] = useState<Set<string>>(new Set());
  const [originalHiddenProducts, setOriginalHiddenProducts] = useState<Set<string>>(new Set());
  const [tickerSpeed, setTickerSpeedState] = useState(TICKER_SPEED_DEFAULT);
  const [originalTickerSpeed, setOriginalTickerSpeed] = useState(TICKER_SPEED_DEFAULT);
  const [expandedVendors, setExpandedVendors] = useState<Set<string>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load preferences on mount
  useEffect(() => {
    async function loadPreferences() {
      try {
        setIsLoading(true);
        const [hidden, speed] = await Promise.all([
          getHiddenFromAPI(),
          getTickerSpeed(),
        ]);
        setHiddenProducts(hidden);
        setOriginalHiddenProducts(new Set(hidden));
        setTickerSpeedState(speed);
        setOriginalTickerSpeed(speed);
      } catch (err) {
        console.error('Failed to load preferences:', err);
        setError('Failed to load preferences');
      } finally {
        setIsLoading(false);
      }
    }
    loadPreferences();
  }, []);

  // Build complete product list per vendor (master list + any from actual items)
  const vendorProductsMap = useMemo(() => {
    const map = new Map<string, Set<string>>();

    // Start with master list
    for (const vendor of vendors) {
      map.set(vendor.id, new Set(vendor.products));
    }

    // Add any products from actual items (in case API returns names not in master list)
    for (const item of items) {
      const vendorId = vendorIdMap[item.vendor] || item.vendor;
      if (!map.has(vendorId)) {
        map.set(vendorId, new Set());
      }
      const products = map.get(vendorId)!;
      for (const service of item.affectedServices) {
        products.add(service);
      }
    }

    return map;
  }, [items]);

  // Track which products have active issues (for highlighting)
  const productsWithIssues = useMemo(() => {
    const set = new Set<string>();
    for (const item of items) {
      const vendorId = vendorIdMap[item.vendor] || item.vendor;
      for (const service of item.affectedServices) {
        set.add(`${vendorId}:${service}`);
      }
    }
    return set;
  }, [items]);

  // Track changes
  useEffect(() => {
    const hiddenChanged = hiddenProducts.size !== originalHiddenProducts.size ||
      [...hiddenProducts].some(p => !originalHiddenProducts.has(p));
    const speedChanged = tickerSpeed !== originalTickerSpeed;
    setHasChanges(hiddenChanged || speedChanged);
  }, [hiddenProducts, originalHiddenProducts, tickerSpeed, originalTickerSpeed]);

  const toggleProduct = useCallback((vendorId: string, product: string) => {
    const key = `${vendorId}:${product}`;
    setHiddenProducts(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const toggleVendorExpanded = useCallback((vendorId: string) => {
    setExpandedVendors(prev => {
      // Accordion behavior - only one open at a time
      if (prev.has(vendorId)) {
        return new Set(); // Close if already open
      } else {
        return new Set([vendorId]); // Open this one, close others
      }
    });
  }, []);

  const getVendorProducts = useCallback((vendor: VendorDefinition): string[] => {
    const products = vendorProductsMap.get(vendor.id) || new Set<string>();
    return [...products].sort();
  }, [vendorProductsMap]);

  const selectAllProducts = useCallback((vendor: VendorDefinition) => {
    const products = getVendorProducts(vendor);
    setHiddenProducts(prev => {
      const next = new Set(prev);
      for (const product of products) {
        next.delete(`${vendor.id}:${product}`);
      }
      return next;
    });
  }, [getVendorProducts]);

  const selectNoProducts = useCallback((vendor: VendorDefinition) => {
    const products = getVendorProducts(vendor);
    setHiddenProducts(prev => {
      const next = new Set(prev);
      for (const product of products) {
        next.add(`${vendor.id}:${product}`);
      }
      return next;
    });
  }, [getVendorProducts]);

  const getVisibleCount = useCallback((vendor: VendorDefinition): number => {
    const products = getVendorProducts(vendor);
    return products.filter(p => !hiddenProducts.has(`${vendor.id}:${p}`)).length;
  }, [getVendorProducts, hiddenProducts]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await Promise.all([
        saveHiddenToAPI(hiddenProducts),
        saveTickerSpeed(tickerSpeed),
      ]);
      setOriginalHiddenProducts(new Set(hiddenProducts));
      setOriginalTickerSpeed(tickerSpeed);
      setHasChanges(false);
      setShowSettings(false);
    } catch (err) {
      console.error('Failed to save preferences:', err);
      setError('Failed to save. Changes saved locally.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setHiddenProducts(new Set(originalHiddenProducts));
    setTickerSpeedState(originalTickerSpeed);
    setHasChanges(false);
    setShowSettings(false);
  };

  const handleResetAll = () => {
    setHiddenProducts(new Set());
    setTickerSpeedState(TICKER_SPEED_DEFAULT);
  };

  const visibleItems = items.filter(item => isItemVisible(item, hiddenProducts));
  const hiddenCount = items.length - visibleItems.length;

  const formatTimeAgo = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    } catch {
      return '';
    }
  };

  return (
    <div className="service-health-modal-overlay" onClick={onClose}>
      <div className="service-health-all-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-row">
            <h2>{showSettings ? 'Configure Alerts' : `Service Health (${visibleItems.length})`}</h2>
            {!showSettings && hiddenCount > 0 && (
              <span className="hidden-count">{hiddenCount} filtered</span>
            )}
          </div>
          <div className="modal-header-actions">
            <button
              className={`settings-toggle ${showSettings ? 'active' : ''}`}
              onClick={() => setShowSettings(!showSettings)}
              title={showSettings ? 'View issues' : 'Configure'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v6m0 6v10M4.22 4.22l4.24 4.24m7.08 7.08l4.24 4.24M1 12h6m6 0h10M4.22 19.78l4.24-4.24m7.08-7.08l4.24-4.24"/>
              </svg>
            </button>
            <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
          </div>
        </div>

        <div className="modal-body">
          {isLoading ? (
            <div className="settings-loading">
              <div className="loading-spinner"></div>
              <p>Loading preferences...</p>
            </div>
          ) : showSettings ? (
            <div className="settings-panel">
              <p className="settings-description">
                Configure ticker display settings.
                {error && <span className="settings-error"> {error}</span>}
              </p>

              {/* Speed slider */}
              <div className="speed-setting">
                <div className="speed-header">
                  <label htmlFor="ticker-speed">Scroll Speed</label>
                  <span className="speed-value">{tickerSpeed} px/s</span>
                </div>
                <div className="speed-slider-row">
                  <span className="speed-label">Slow</span>
                  <input
                    id="ticker-speed"
                    type="range"
                    min={TICKER_SPEED_MIN}
                    max={TICKER_SPEED_MAX}
                    value={tickerSpeed}
                    onChange={(e) => setTickerSpeedState(Number(e.target.value))}
                    className="speed-slider"
                  />
                  <span className="speed-label">Fast</span>
                </div>
              </div>

              <div className="settings-section-divider"></div>

              <p className="settings-description-sub">
                Choose which products to monitor. Unchecked products won't appear in the ticker.
              </p>
              <div className="vendor-products-list">
                {vendors.map(vendor => {
                  const products = getVendorProducts(vendor);
                  const isExpanded = expandedVendors.has(vendor.id);
                  const visibleCount = getVisibleCount(vendor);
                  const totalCount = products.length;
                  const allVisible = visibleCount === totalCount;
                  const noneVisible = visibleCount === 0;

                  return (
                    <div key={vendor.id} className="vendor-group">
                      <button
                        className="vendor-header"
                        onClick={() => toggleVendorExpanded(vendor.id)}
                      >
                        <span
                          className="vendor-icon"
                          style={{ backgroundColor: vendor.color }}
                        >
                          {vendor.icon}
                        </span>
                        <span className="vendor-name">{vendor.name}</span>
                        <span className="vendor-count">
                          {visibleCount}/{totalCount}
                        </span>
                        <span className="vendor-expand-icon">
                          {isExpanded ? '▼' : '▶'}
                        </span>
                      </button>

                      {isExpanded && (
                        <div className="product-section">
                          <div className="product-actions">
                            <button
                              className={`product-action-btn ${allVisible ? 'active' : ''}`}
                              onClick={() => selectAllProducts(vendor)}
                            >
                              All
                            </button>
                            <button
                              className={`product-action-btn ${noneVisible ? 'active' : ''}`}
                              onClick={() => selectNoProducts(vendor)}
                            >
                              None
                            </button>
                          </div>
                          <div className="product-grid">
                            {products.map(product => {
                              const key = `${vendor.id}:${product}`;
                              const isHidden = hiddenProducts.has(key);
                              const hasIssue = productsWithIssues.has(key);

                              return (
                                <label
                                  key={product}
                                  className={`product-item ${hasIssue ? 'has-issue' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!isHidden}
                                    onChange={() => toggleProduct(vendor.id, product)}
                                  />
                                  <span className="product-name">{product}</span>
                                  {hasIssue && <span className="product-issue-dot" />}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="settings-footer">
                <button className="reset-btn" onClick={handleResetAll}>
                  Reset All
                </button>
                <div className="settings-footer-right">
                  <button className="cancel-btn" onClick={handleCancel} disabled={isSaving}>
                    Cancel
                  </button>
                  <button
                    className="save-btn"
                    onClick={handleSave}
                    disabled={!hasChanges || isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="all-items-list">
              {visibleItems.length === 0 ? (
                <div className="no-items">
                  {items.length === 0
                    ? 'All systems operational'
                    : 'All items filtered. Click gear to configure.'}
                </div>
              ) : (
                visibleItems.map(item => {
                  const vendor = vendors.find(v =>
                    v.id === (vendorIdMap[item.vendor] || item.vendor)
                  );

                  return (
                    <button
                      key={item.id}
                      className="all-items-row"
                      onClick={() => onItemClick(item)}
                    >
                      <span
                        className="all-items-vendor"
                        style={{ backgroundColor: vendor?.color || '#666' }}
                      >
                        {vendor?.icon || item.vendorIcon}
                      </span>
                      <div className="all-items-info">
                        <span className="all-items-title">{item.title}</span>
                        <span className="all-items-meta">
                          {item.affectedServices.slice(0, 2).join(', ')}
                          {item.affectedClients && item.affectedClients.length > 0 && (
                            <> · {item.affectedClients.length} client{item.affectedClients.length !== 1 ? 's' : ''}</>
                          )}
                        </span>
                      </div>
                      <span className="all-items-time">{formatTimeAgo(item.updatedAt)}</span>
                      <span
                        className="all-items-severity"
                        style={{
                          backgroundColor: severityConfig[item.severity].bgColor,
                          color: severityConfig[item.severity].color
                        }}
                      >
                        {severityConfig[item.severity].label}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
